import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Badge,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Text,
  VStack,
  useToast,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiLayers, FiLink2 } from "react-icons/fi";
import SearchableDropdown from "../components/SearchableDropdown";
import WysiwygEditor from "../components/WysiwygEditor";
import api from "../services/api";
import { employeeService } from "../modules/employee/employeeService";
import {
  projectService,
  sprintService,
  milestoneService,
} from "../services/edosService";
import { resolveTenantId } from "../utils/tenant";
import { useTicketConstants } from "../hooks/useTicketConstants";

const getDisplayName = (entity) => {
  if (!entity) return "";
  if (entity.name) return entity.name;
  if (entity.clientName) return entity.clientName;
  if (entity.companyName) return entity.companyName;

  const fullName = [entity.firstName, entity.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    fullName ||
    entity.employeeId ||
    entity.clientId ||
    entity.email ||
    String(entity._id || "")
  );
};

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload;

  const directCandidates = [
    payload?.data,
    payload?.data?.data,
    payload?.clients,
    payload?.employees,
    payload?.data?.clients,
    payload?.data?.employees,
    payload?.rows,
    payload?.items,
    payload?.results,
    payload?.list,
  ];

  for (const candidate of directCandidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  // Last-resort scan for the first array value in nested objects.
  if (payload && typeof payload === "object") {
    for (const value of Object.values(payload)) {
      if (Array.isArray(value)) return value;
      if (value && typeof value === "object") {
        for (const inner of Object.values(value)) {
          if (Array.isArray(inner)) return inner;
        }
      }
    }
  }

  return [];
};

const normalizeOptions = (rows = []) => {
  const mapped = rows
    .map((row) => ({
      value: row?._id,
      label: getDisplayName(row),
    }))
    .filter((item) => Boolean(item.value) && Boolean(item.label));

  const uniqueByValue = Array.from(
    new Map(mapped.map((item) => [item.value, item])).values(),
  );

  return uniqueByValue.sort((a, b) => a.label.localeCompare(b.label));
};

const pickRandom = (items = []) => {
  if (!items.length) return null;
  return items[Math.floor(Math.random() * items.length)];
};

const toDateTimeLocalValue = (date) => {
  const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16);
};

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const resolveClientIdFromTenant = (rows = [], tenantSlug = "") => {
  const normalizedTenant = normalizeKey(tenantSlug);
  if (!normalizedTenant || !Array.isArray(rows) || rows.length === 0) {
    return "";
  }

  const matched = rows.find((row) => {
    const candidateKeys = [
      row?.slug,
      row?.tenantSlug,
      row?.name,
      row?.clientName,
      row?.companyName,
      row?.clientId,
    ].map(normalizeKey);

    return candidateKeys.includes(normalizedTenant);
  });

  return matched?._id || "";
};

const buildAutoClientPayload = (tenantSlug = "") => {
  const base = normalizeKey(tenantSlug) || "default-client";
  const safeBase = base.replace(/[^a-z0-9-]/g, "") || "default-client";
  const generatedClientId = `CLI${String(Date.now()).slice(-6)}`;
  return {
    clientId: generatedClientId,
    name: tenantSlug || "Default Client",
    email: `${safeBase}.${Date.now()}@example.com`,
    phone: "0000000000",
    address: "Auto-created by ticketing",
    status: "Active",
  };
};

export default function CreateTicketPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const tenantSlug = resolveTenantId();

  // Fetch ticket constants from server
  const {
    PRIORITIES = ["Low", "Medium", "High", "Urgent"],
    SEVERITIES = ["Low", "Medium", "High", "Critical"],
  } = useTicketConstants();

  const [loadingContext, setLoadingContext] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectSprints, setProjectSprints] = useState([]);
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "Medium",
    severity: "Medium",
    category: "",
    requestedBy: "",
    assignedTo: "",
    dueDate: "",
    projectId: "",
    sprintId: "",
    milestoneId: "",
  });

  React.useEffect(() => {
    const load = async () => {
      setLoadingContext(true);
      try {
        const [employeesRes, clientsRes] = await Promise.all([
          employeeService.getAllEmployees(),
          api.get("/clients/getAllClients"),
        ]);
        const projectsRes = await projectService.list();

        const resolvedEmployees = extractRows(employeesRes);
        const resolvedClients = extractRows(clientsRes?.data);
        const resolvedProjects =
          projectsRes?.data?.data || projectsRes?.data?.projects || [];

        setEmployees(resolvedEmployees);
        setClients(resolvedClients);
        setProjects(resolvedProjects);
      } catch (error) {
        toast({
          title: "Failed to load form data",
          description:
            error.response?.data?.message || "Please refresh and try again.",
          status: "error",
        });
      } finally {
        setLoadingContext(false);
      }
    };

    load();
  }, [toast]);

  React.useEffect(() => {
    const loadProjectScopedItems = async () => {
      if (!form.projectId) {
        setProjectSprints([]);
        setProjectMilestones([]);
        setForm((prev) => ({ ...prev, sprintId: "", milestoneId: "" }));
        return;
      }

      try {
        const [sprintsRes, milestonesRes] = await Promise.all([
          sprintService.listByProject(form.projectId),
          milestoneService.listByProject(form.projectId),
        ]);

        const sprintRows =
          sprintsRes?.data?.data || sprintsRes?.data?.sprints || [];
        const milestoneRows =
          milestonesRes?.data?.data || milestonesRes?.data?.milestones || [];

        setProjectSprints(sprintRows);
        setProjectMilestones(milestoneRows);
      } catch {
        setProjectSprints([]);
        setProjectMilestones([]);
      }
    };

    loadProjectScopedItems();
  }, [form.projectId]);

  const titleBg = useColorModeValue(
    "linear-gradient(120deg, #f5fbff 0%, #effaf3 100%)",
    "gray.800",
  );
  const pageBg = useColorModeValue(
    "linear-gradient(140deg, #f7fbff 0%, #eef8f4 50%, #fff8ee 100%)",
    "gray.900",
  );
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const subtleText = useColorModeValue("gray.600", "gray.300");

  const clientOptions = useMemo(() => normalizeOptions(clients), [clients]);

  const employeeOptions = useMemo(
    () => normalizeOptions(employees),
    [employees],
  );

  const handleQuickFill = async () => {
    let latestEmployees = employees;
    let latestClients = clients;

    // Lazy-load lookup data if quick fill is clicked before lists are available.
    if (!latestEmployees.length || !latestClients.length) {
      try {
        const [employeesRes, clientsRes] = await Promise.all([
          employeeService.getAllEmployees(),
          api.get("/clients/getAllClients"),
        ]);

        latestEmployees = extractRows(employeesRes);
        latestClients = extractRows(clientsRes?.data);
        setEmployees(latestEmployees);
        setClients(latestClients);
      } catch (error) {
        toast({
          title: "Unable to load random selection data",
          description: "Please refresh and try quick fill again.",
          status: "error",
        });
        return;
      }
    }

    const latestEmployeeOptions = normalizeOptions(latestEmployees);

    if (!latestEmployeeOptions.length) {
      toast({
        title: "Quick fill needs at least one employee",
        status: "warning",
      });
      return;
    }

    const priorityOptions = PRIORITIES.length
      ? PRIORITIES
      : ["Low", "Medium", "High", "Urgent"];
    const severityOptions = SEVERITIES.length
      ? SEVERITIES
      : ["Low", "Medium", "High", "Critical"];
    const categoryOptions = ["Access", "Hardware", "Software", "Network"];
    const titleOptions = [
      "VPN access not working",
      "Payroll PDF export failure",
      "Laptop battery health issue",
      "SSO timeout after login",
      "Attendance sync delay",
      "Email delivery bounce alert",
    ];
    const descriptionOptions = [
      "Issue reproduced in production and impacts multiple users. Please investigate and provide workaround.",
      "Intermittent behavior observed across two departments during peak hours.",
      "Customer reported high urgency due to blocked daily operations.",
      "Test scenario generated for QA validation and workflow checks.",
    ];

    const randomRequester = pickRandom(latestEmployeeOptions);
    const randomAssignee =
      Math.random() > 0.25 ? pickRandom(latestEmployeeOptions) : null;
    const futureHours = 8 + Math.floor(Math.random() * 72);

    setForm((prev) => ({
      ...prev,
      title: `${pickRandom(titleOptions)} [TC-${Math.floor(1000 + Math.random() * 9000)}]`,
      description: pickRandom(descriptionOptions),
      priority: pickRandom(priorityOptions),
      severity: pickRandom(severityOptions),
      category: pickRandom(categoryOptions),
      requestedBy: randomRequester?.value || "",
      assignedTo: randomAssignee?.value || "",
      dueDate: toDateTimeLocalValue(
        new Date(Date.now() + futureHours * 60 * 60 * 1000),
      ),
    }));

    toast({ title: "Quick fill complete", status: "success" });
  };

  const handleCreate = async () => {
    if (!form.title || form.title.trim().length < 3) {
      toast({
        title: "Title is required (min 3 characters)",
        status: "warning",
      });
      return;
    }
    let resolvedClient =
      resolveClientIdFromTenant(clients, tenantSlug) ||
      clientOptions[0]?.value ||
      "";
    if (!resolvedClient) {
      try {
        const clientsRes = await api.get("/clients/getAllClients");
        const refreshedClients = extractRows(clientsRes?.data || clientsRes);
        const refreshedClientOptions = normalizeOptions(refreshedClients);
        setClients(refreshedClients);
        resolvedClient =
          resolveClientIdFromTenant(refreshedClients, tenantSlug) ||
          refreshedClientOptions[0]?.value ||
          "";
      } catch (error) {
        // Keep resolvedClient empty and show actionable message below.
      }
    }

    if (!resolvedClient) {
      try {
        const autoClientPayload = buildAutoClientPayload(tenantSlug);
        const createClientRes = await api.post(
          "/clients/createClient",
          autoClientPayload,
        );

        const createdClient =
          createClientRes?.data?.data || createClientRes?.data;
        if (createdClient?._id) {
          resolvedClient = createdClient._id;
          setClients((prev) => [...prev, createdClient]);
          toast({
            title: "Default client created automatically",
            status: "info",
          });
        }
      } catch (error) {
        // Keep resolvedClient empty and show actionable message below.
      }
    }

    if (!resolvedClient) {
      toast({
        title: "No client available for ticket creation",
        description:
          "Could not find or auto-create a client. Please create one in client management and try again.",
        status: "warning",
      });
      return;
    }
    if (!form.requestedBy) {
      toast({ title: "Requested By is required", status: "warning" });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        severity: form.severity,
        category: form.category,
        requestedBy: form.requestedBy,
        client: resolvedClient,
        assignedTo: form.assignedTo || null,
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
        projectId: form.projectId || null,
        sprintId: form.sprintId || null,
        milestoneId: form.milestoneId || null,
      };

      await api.post("/api/tickets", payload);
      toast({ title: "Ticket created successfully", status: "success" });
      navigate("/Ticketing");
    } catch (error) {
      toast({
        title: "Failed to create ticket",
        description:
          error.response?.data?.message || "Please review the form fields.",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingContext) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box p={{ base: 3, md: 5 }} bg={pageBg} minH="100vh" position="relative">
      <Box
        position="absolute"
        top={{ base: "8px", md: "12px" }}
        right={{ base: "8px", md: "22px" }}
        w={{ base: "100px", md: "170px" }}
        h={{ base: "100px", md: "170px" }}
        borderRadius="full"
        bg="blue.100"
        opacity={0.25}
        filter="blur(24px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom={{ base: "16px", md: "24px" }}
        left={{ base: "8px", md: "18px" }}
        w={{ base: "90px", md: "150px" }}
        h={{ base: "90px", md: "150px" }}
        borderRadius="full"
        bg="orange.100"
        opacity={0.3}
        filter="blur(24px)"
        pointerEvents="none"
      />

      <Box maxW="100%" mx="auto" position="relative" zIndex={1}>
        <Box
          p={5}
          bg={titleBg}
          border="1px solid"
          borderColor={cardBorder}
          borderRadius="2xl"
          mb={5}
          boxShadow="sm"
        >
          <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
            <Box>
              <Heading size="lg">Create New Ticket</Heading>
              <Text mt={2} color={subtleText} maxW="720px">
                Capture issue details, assign ownership, and optionally link
                this ticket to Enterprise Delivery OS planning artifacts.
              </Text>
            </Box>
            <Badge
              colorScheme="blue"
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
            >
              Enterprise Delivery OS
            </Badge>
          </HStack>
          <Text mt={3} color="gray.600">
            Fill required fields: Title and Requested By. Client is assigned
            automatically.
          </Text>
        </Box>

        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={cardBorder}
          borderRadius="2xl"
          p={{ base: 4, md: 6 }}
          boxShadow="lg"
        >
          <VStack spacing={6} align="stretch">
            <Box>
              <HStack mb={4} spacing={2}>
                <Icon as={FiLayers} color="blue.500" boxSize={5} />
                <Text
                  fontWeight="600"
                  fontSize="sm"
                  color={useColorModeValue("gray.700", "gray.100")}
                >
                  Basic Details
                </Text>
              </HStack>
              <VStack spacing={5} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500">
                    Title
                  </FormLabel>
                  <Input
                    placeholder="e.g. Payroll export blocked for finance team"
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    borderColor={cardBorder}
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px blue.400",
                    }}
                    size="md"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={3}>
                    Description
                  </FormLabel>
                  <Box
                    border="1px solid"
                    borderColor={cardBorder}
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    <WysiwygEditor
                      value={form.description}
                      onChange={(html) =>
                        setForm((prev) => ({
                          ...prev,
                          description: html,
                        }))
                      }
                      placeholder="Provide context, impact, and any steps to reproduce..."
                    />
                  </Box>
                </FormControl>
              </VStack>
            </Box>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
              <Box
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="lg"
                p={5}
                bg={useColorModeValue("gray.50", "gray.700")}
              >
                <HStack mb={4} spacing={2}>
                  <Box w={3} h={3} borderRadius="full" bg="purple.500" />
                  <Text
                    fontWeight="600"
                    fontSize="sm"
                    color={useColorModeValue("gray.700", "gray.100")}
                  >
                    Ticket Metadata
                  </Text>
                </HStack>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Priority
                    </FormLabel>
                    <Select
                      value={form.priority}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          priority: e.target.value,
                        }))
                      }
                      borderColor={cardBorder}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px blue.400",
                      }}
                    >
                      {PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Severity
                    </FormLabel>
                    <Select
                      value={form.severity}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          severity: e.target.value,
                        }))
                      }
                      borderColor={cardBorder}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px blue.400",
                      }}
                    >
                      {SEVERITIES.map((severity) => (
                        <option key={severity} value={severity}>
                          {severity}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Category
                    </FormLabel>
                    <Select
                      placeholder="Select category"
                      value={form.category}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      borderColor={cardBorder}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px blue.400",
                      }}
                    >
                      <option value="Access">Access</option>
                      <option value="Hardware">Hardware</option>
                      <option value="Software">Software</option>
                      <option value="Network">Network</option>
                    </Select>
                  </FormControl>
                </VStack>
              </Box>

              <Box
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="lg"
                p={5}
                bg={useColorModeValue("gray.50", "gray.700")}
              >
                <HStack mb={4} spacing={2}>
                  <Box w={3} h={3} borderRadius="full" bg="orange.500" />
                  <Text
                    fontWeight="600"
                    fontSize="sm"
                    color={useColorModeValue("gray.700", "gray.100")}
                  >
                    Assignment
                  </Text>
                </HStack>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Requested By
                    </FormLabel>
                    <SearchableDropdown
                      placeholder="Select requestor"
                      options={employeeOptions}
                      onSelect={(value) =>
                        setForm((prev) => ({ ...prev, requestedBy: value }))
                      }
                    />
                  </FormControl>
                  {employeeOptions.length === 0 && (
                    <Text fontSize="xs" color="orange.500" w="100%">
                      No employees available to select.
                    </Text>
                  )}

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Assigned To
                    </FormLabel>
                    <SearchableDropdown
                      placeholder="Select assignee"
                      options={employeeOptions}
                      onSelect={(value) =>
                        setForm((prev) => ({ ...prev, assignedTo: value }))
                      }
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500">
                      Due Date
                    </FormLabel>
                    <Input
                      type="datetime-local"
                      value={form.dueDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          dueDate: e.target.value,
                        }))
                      }
                      borderColor={cardBorder}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px blue.400",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>
            </SimpleGrid>

            <Divider />

            <Box
              border="1px solid"
              borderColor={cardBorder}
              borderRadius="lg"
              p={5}
              bg={useColorModeValue("blue.50", "gray.700")}
            >
              <HStack mb={3} spacing={2}>
                <Icon as={FiLink2} color="blue.500" boxSize={5} />
                <Text
                  fontWeight="600"
                  fontSize="sm"
                  color={useColorModeValue("gray.700", "gray.100")}
                >
                  EDOS Linkage
                </Text>
                <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                  Compulsory for sprint/milestone
                </Badge>
              </HStack>
              <Text fontSize="sm" color={subtleText} mb={4} lineHeight="1.6">
                Link this ticket to delivery planning entities for sprint
                execution and milestone tracking.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500">
                    Project
                  </FormLabel>
                  <Select
                    placeholder="Project"
                    value={form.projectId}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        projectId: e.target.value,
                        sprintId: "",
                        milestoneId: "",
                      }))
                    }
                    borderColor={cardBorder}
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px blue.400",
                    }}
                  >
                    {projects.map((project) => (
                      <option key={project._id} value={project._id}>
                        {project.projectName || project.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500">
                    Sprint
                  </FormLabel>
                  <Select
                    placeholder="Sprint"
                    value={form.sprintId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, sprintId: e.target.value }))
                    }
                    isDisabled={!form.projectId}
                    borderColor={cardBorder}
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px blue.400",
                    }}
                  >
                    {projectSprints.map((sprint) => (
                      <option key={sprint._id} value={sprint._id}>
                        {sprint.sprintName || sprint.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500">
                    Milestone
                  </FormLabel>
                  <Select
                    placeholder="Milestone"
                    value={form.milestoneId}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        milestoneId: e.target.value,
                      }))
                    }
                    isDisabled={!form.projectId}
                    borderColor={cardBorder}
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px blue.400",
                    }}
                  >
                    {projectMilestones.map((milestone) => (
                      <option key={milestone._id} value={milestone._id}>
                        {milestone.milestoneName ||
                          milestone.title ||
                          milestone.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Box>

            <HStack justify="flex-end" pt={4} spacing={3}>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={handleQuickFill}
                fontSize="sm"
                fontWeight="500"
              >
                Quick Fill Test Case
              </Button>
              <Button
                variant="ghost"
                colorScheme="gray"
                onClick={() => navigate("/Ticketing")}
                fontSize="sm"
                fontWeight="500"
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isLoading={isLoading}
                onClick={handleCreate}
                fontSize="sm"
                fontWeight="600"
                px={6}
              >
                Create Ticket
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}
